/**
 * 004 — Adapter: (invitación + evento + invitado?) → payload canónico.
 * El estilo viene de la invitación (`template_id`); nombres/fecha del Evento (006);
 * campos ✍️/🎚 de columnas + `sections` JSONB; el invitado 🔗 de `access_code` (002).
 * El payload NO contiene ⚙️ (derived.ts) ni 🏛 (system-catalog.ts).
 */
import {
  InvitationPayload,
  Interruptores,
  EventoItem,
  RetratosModo,
  InvitadoPayload,
} from './types';
import { toEstilo } from './styles';
import { formatDeadlineDate } from '@/lib/event/rsvpDeadline';

/** Vista estructural de la fila `wedding_invitations` que usa el adapter. */
export interface AdapterInvitation {
  template_id?: string | null;
  religious_ceremony_enabled?: boolean | null;
  religious_ceremony_name?: string | null;
  religious_ceremony_address?: string | null;
  religious_ceremony_time?: string | null;
  religious_ceremony_map_url?: string | null;
  civil_ceremony_enabled?: boolean | null;
  civil_ceremony_name?: string | null;
  civil_ceremony_address?: string | null;
  civil_ceremony_time?: string | null;
  civil_ceremony_map_url?: string | null;
  party_name?: string | null;
  party_address?: string | null;
  party_time?: string | null;
  party_map_url?: string | null;
  gifts_message?: string | null;
  bank_transfer_enabled?: boolean | null;
  bank_account_holder?: string | null;
  bank_account_number?: string | null;
  bank_name?: string | null;
  cash_envelope_enabled?: boolean | null;
  gift_registry_url?: string | null;
  qr_transfer_enabled?: boolean | null;
  qr_image_url?: string | null;
  couple_gallery_enabled?: boolean | null;
  guest_photos_enabled?: boolean | null;
  children_allowed?: boolean | null;
  background_music_url?: string | null;
  itinerary?: unknown;
  /** JSONB aditivo con los campos de sección sin columna propia. */
  sections?: Record<string, unknown> | null;
}

export interface AdapterEvent {
  bride_name?: string | null;
  groom_name?: string | null;
  wedding_date?: string | null;
  /** 008 · US10 — 'bride_first' (default) | 'groom_first'. */
  couple_name_order?: string | null;
  /** 008 · US1 — instante límite de RSVP; su fecha se muestra en la invitación (fuente única). */
  rsvp_deadline?: string | null;
}

export interface AdapterGuest {
  guest_name?: string | null;
  /** 008 · US4 — rótulo de invitación (reemplaza el nombre en el saludo si está presente). */
  display_label?: string | null;
  passes_count?: number | null;
}

function bool(v: unknown, dflt: boolean): boolean {
  return typeof v === 'boolean' ? v : dflt;
}

function sec<T>(sections: Record<string, unknown> | null | undefined, key: string): T | undefined {
  if (!sections) return undefined;
  return sections[key] as T | undefined;
}

function buildInterruptores(
  inv: AdapterInvitation,
  s: Record<string, unknown> | null | undefined,
): Interruptores {
  return {
    retratos: (sec<RetratosModo>(s, 'retratos') ?? 'pareja'),
    fotosLugares: bool(sec(s, 'fotosLugares'), false),
    fotoVestimenta: bool(sec(s, 'fotoVestimenta'), false),
    galeria: bool(inv.couple_gallery_enabled, true),
    fotosInvitados: bool(inv.guest_photos_enabled, true),
    ceremoniaReligiosa: bool(inv.religious_ceremony_enabled, false),
    ceremoniaCivil: bool(inv.civil_ceremony_enabled, false),
    ninosPermitidos: bool(inv.children_allowed, true),
    transferenciaQR: bool(inv.qr_transfer_enabled, false),
    coloresSugeridos: bool(sec(s, 'coloresSugeridos'), false),
    coloresReservados: bool(sec(s, 'coloresReservados'), false),
    regalos: bool(sec(s, 'regalos'), true),
    mesaRegalos: bool(sec(s, 'mesaRegalos'), Boolean(inv.gift_registry_url)),
    transferencia: bool(inv.bank_transfer_enabled, false),
    efectivoSobre: bool(inv.cash_envelope_enabled, false),
    listaRegalos: bool(sec(s, 'listaRegalos'), Boolean(inv.gift_registry_url)),
  };
}

/** Normaliza una hora a `HH:MM` (quita los segundos `:SS` que trae la BD, p. ej. `17:30:00`). */
function hhmm(t: string | null | undefined): string | undefined {
  if (!t) return undefined;
  const m = /^(\d{1,2}:\d{2})(?::\d{2})?/.exec(t.trim());
  return m ? m[1] : t;
}

function buildEventos(inv: AdapterInvitation, s: Record<string, unknown> | null | undefined): EventoItem[] {
  const notas = sec<Record<string, string>>(s, 'eventoNotas') ?? {};
  const fotosObj = sec<{ sedes?: Record<string, string> }>(s, 'fotos');
  const fotos = fotosObj?.sedes ?? {};
  const out: EventoItem[] = [];
  if (inv.religious_ceremony_enabled) {
    out.push({
      tipo: 'religiosa',
      hora: hhmm(inv.religious_ceremony_time),
      sede: inv.religious_ceremony_name ?? undefined,
      direccion: inv.religious_ceremony_address ?? undefined,
      nota: notas.religiosa,
      mapaUrl: inv.religious_ceremony_map_url ?? undefined,
      foto: fotos.religiosa,
    });
  }
  if (inv.civil_ceremony_enabled) {
    out.push({
      tipo: 'civil',
      hora: hhmm(inv.civil_ceremony_time),
      sede: inv.civil_ceremony_name ?? undefined,
      direccion: inv.civil_ceremony_address ?? undefined,
      nota: notas.civil,
      mapaUrl: inv.civil_ceremony_map_url ?? undefined,
      foto: fotos.civil,
    });
  }
  // La recepción siempre aparece (diccionario §8) si hay datos de fiesta.
  if (inv.party_name || inv.party_address || inv.party_time) {
    out.push({
      tipo: 'recepcion',
      hora: hhmm(inv.party_time),
      sede: inv.party_name ?? undefined,
      direccion: inv.party_address ?? undefined,
      nota: notas.recepcion,
      mapaUrl: inv.party_map_url ?? undefined,
      foto: fotos.recepcion,
    });
  }
  return out;
}

function buildInvitado(guest?: AdapterGuest | null): InvitadoPayload | undefined {
  if (!guest || !guest.guest_name) return undefined;
  // 008 · US4 — el rótulo de invitación (display_label) reemplaza el nombre en el saludo.
  return { nombre: guest.display_label ?? guest.guest_name, pases: guest.passes_count ?? 1 };
}

export interface BuildPayloadInput {
  invitation: AdapterInvitation;
  event: AdapterEvent;
  guest?: AdapterGuest | null;
}

/** Normaliza items de itinerario (soporta el shape legacy {time,title,description}). */
function normalizeItinerario(raw: unknown): InvitationPayload['itinerario'] {
  if (!Array.isArray(raw)) return [];
  return raw.map((r) => {
    const it = (r ?? {}) as Record<string, unknown>;
    return {
      momento: (it.momento ?? it.title ?? '') as string,
      momentoLibre: it.momentoLibre as string | undefined,
      hora: hhmm((it.hora ?? it.time) as string | undefined),
      detalle: (it.detalle ?? it.description) as string | undefined,
    };
  }).filter((it) => it.momento || it.hora);
}

export function buildPayload({ invitation, event, guest }: BuildPayloadInput): InvitationPayload {
  const s = invitation.sections ?? null;
  // Prioridad: itinerario del editor DS (sections, con momentos de catálogo) → legacy.
  const secItin = normalizeItinerario(sec(s, 'itinerario'));
  const itin = secItin.length > 0 ? secItin : normalizeItinerario(invitation.itinerary);

  return {
    estilo: toEstilo(invitation.template_id),
    boda: {
      fecha: event.wedding_date ?? null,
      ordenNombres: event.couple_name_order === 'groom_first' ? 'groom_first' : 'bride_first',
    },
    novia: {
      nombreCorto: event.bride_name ?? '',
      nombreCompleto: sec<string>(s, 'noviaNombreCompleto'),
    },
    novio: {
      nombreCorto: event.groom_name ?? '',
      nombreCompleto: sec<string>(s, 'novioNombreCompleto'),
    },
    historia: {
      texto: sec<string>(s, 'historiaTexto'),
      frase: sec<string>(s, 'historiaFrase'),
    },
    familia: sec<Array<{ rol: string; nombre: string }>>(s, 'familia'),
    eventos: buildEventos(invitation, s),
    itinerario: itin,
    vestimenta: sec<InvitationPayload['vestimenta']>(s, 'vestimenta'),
    album: sec<InvitationPayload['album']>(s, 'album'),
    regalos: {
      mensaje: invitation.gifts_message ?? sec<string>(s, 'regalosMensaje'),
      tiendaUrl: invitation.gift_registry_url ?? undefined,
      banco: invitation.bank_name ?? undefined,
      titular: invitation.bank_account_holder ?? undefined,
      cuenta: invitation.bank_account_number ?? undefined,
      qrUrl: invitation.qr_image_url ?? sec<string>(s, 'regalosQrUrl'),
      moneda: sec<string>(s, 'regalosMoneda') ?? 'Bs',
      sugerencias: sec<InvitationPayload['regalos']['sugerencias']>(s, 'regalosSugerencias'),
    },
    // 008 · US1 — fuente única: la fecha límite mostrada deriva del `rsvp_deadline` del evento
    // (formateada TZ-safe). Cae al texto libre legacy solo si el evento aún no la define.
    rsvp: {
      fechaLimite: event.rsvp_deadline
        ? formatDeadlineDate(event.rsvp_deadline)
        : sec<string>(s, 'rsvpFechaLimite'),
    },
    musica: { url: invitation.background_music_url ?? undefined },
    fotos: sec<InvitationPayload['fotos']>(s, 'fotos') ?? {},
    interruptores: buildInterruptores(invitation, s),
    invitado: buildInvitado(guest),
  };
}
